import { Container, Graphics, Text } from "pixi.js"
import { IMouse, MouseState } from "../../../../client_models"
import { SCALER } from "../../../../client_util"
import { TextRenderer } from "../text/text_renderer"

// The location chooser: while it is up the pointer stops meaning "attack" and starts meaning
// "here". Put up by the defender's "Hold Position" order, which wants a spot on the ground at FINE
// (pixel) precision -- the build preview's tile grid is too coarse to post a guard with.
//
// Drawn rather than sprited (unlike the bow's crosshair, which is an atlas frame) so the whole
// reticle can carry a state color -- amber for ground the NPC can stand on, red for ground it
// can't -- without a second texture, and so it costs one Graphics on a weak GPU.
//
// GEOMETRY. This is a direct child of the stage, which carries `scale.y = SCALER` (1.3): the same
// vertical stretch every GUI widget here lives with. A circle drawn in that frame reads as an egg,
// so the container undoes the stretch (scale.y = 1/SCALER) and positions itself in stage-local
// space (global.y / SCALER). The ground ellipse underneath is squashed DELIBERATELY on top of that,
// so it lies on the ground plane the way an entity's foot shadow does.

const RETICLE_COLOR = 0xf2a94e // warm amber -- the command wheel's rim, so the ember UI reads as one set
const BLOCKED_COLOR = 0xe8705c // muted red: this ground can't be stood on
const CAPTION_COLOR = 0xfff3e0

const RING_RADIUS = 11
const TICK_GAP = 4 // clear space between the ring and the cross ticks
const TICK_LENGTH = 6
const GROUND_RADIUS_X = 17
const GROUND_RADIUS_Y = 6
const CAPTION_OFFSET_Y = 20

export class LocationPicker extends Container {
    private reticle: Graphics
    private caption: Text
    // Two captions, one line: what the pick is FOR, and — over ground nothing can stand on — why
    // clicking here will not take. The refusal replaces the prompt rather than sitting beside it,
    // so the line never has to compete with itself for the player's eye.
    private prompt: string = ""
    private refusal: string = ""
    private blocked: boolean = false
    // The mouse is bound lazily and exactly once: GuiManager.init() runs before the renderer has
    // finished constructing itself, so there is no mouse to listen to yet, and a listener added per
    // show() would stack up one copy per order given.
    private boundMouse?: IMouse

    constructor() {
        super()

        // Undo the stage's vertical stretch so the reticle is round (see GEOMETRY above).
        this.scale.set(1, 1 / SCALER)
        this.zIndex = 59000 // over the world and the HUD, under the command wheel (60000)
        this.visible = false

        this.reticle = new Graphics()
        this.addChild(this.reticle)

        this.caption = new TextRenderer().role("compactLabel").bold().align("center").color(CAPTION_COLOR).render() as Text
        this.addChild(this.caption)

        this.redraw()
    }

    // Idempotent: the first show() binds, every later one is a no-op.
    bindMouse = (mouse?: IMouse) => {
        if (this.boundMouse || !mouse) {
            return
        }
        this.boundMouse = mouse
        mouse.addListener(this.followPointer)
    }

    private followPointer = (state: MouseState) => {
        if (!this.visible) {
            return
        }
        this.x = state.position.x
        this.y = state.position.y / SCALER
    }

    show = (caption: string, blockedCaption: string) => {
        this.prompt = caption
        this.refusal = blockedCaption
        this.blocked = false
        this.layoutCaption()
        this.visible = true
        // Jump straight to wherever the pointer already is instead of waiting for the next move --
        // otherwise the reticle appears at the last spot it was used, which is somewhere else.
        if (this.boundMouse) {
            const position = this.boundMouse.state().position
            this.x = position.x
            this.y = position.y / SCALER
        }
    }

    hide = () => {
        this.visible = false
    }

    isVisible = (): boolean => this.visible

    isBlocked = (): boolean => this.blocked

    // The line currently under the reticle -- the prompt, or the refusal that has replaced it.
    captionText = (): string => this.caption.text

    setBlocked = (blocked: boolean) => {
        if (this.blocked === blocked) {
            return
        }
        this.blocked = blocked
        this.redraw()
    }

    // The line under the reticle: the prompt, or the refusal in its place while the ground is
    // blocked. Re-centred every time because the two read at different widths.
    private layoutCaption = () => {
        this.caption.text = this.blocked ? this.refusal : this.prompt
        this.caption.x = -this.caption.width / 2
        this.caption.y = CAPTION_OFFSET_Y
        this.caption.tint = this.blocked ? BLOCKED_COLOR : CAPTION_COLOR
    }

    private redraw = () => {
        const color = this.blocked ? BLOCKED_COLOR : RETICLE_COLOR
        const graphics = this.reticle
        graphics.clear()

        // The patch of ground being chosen, lying flat.
        graphics.lineStyle(1, color, this.blocked ? 0.9 : 0.5)
        graphics.drawEllipse(0, 0, GROUND_RADIUS_X, GROUND_RADIUS_Y)

        // The reticle proper: a ring with four ticks aimed at its center.
        graphics.lineStyle(1.5, color, 1)
        graphics.drawCircle(0, 0, RING_RADIUS)
        const directions: Array<[number, number]> = [
            [1, 0],
            [-1, 0],
            [0, 1],
            [0, -1],
        ]
        directions.forEach(([dx, dy]) => {
            graphics.moveTo(dx * (RING_RADIUS + TICK_GAP), dy * (RING_RADIUS + TICK_GAP))
            graphics.lineTo(dx * (RING_RADIUS + TICK_GAP + TICK_LENGTH), dy * (RING_RADIUS + TICK_GAP + TICK_LENGTH))
        })

        graphics.beginFill(color, 1)
        graphics.drawCircle(0, 0, 1.5)
        graphics.endFill()

        this.layoutCaption()
    }
}
